import { useEffect, lazy, Suspense } from 'react';
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  type Location,
} from 'react-router-dom';
import { useHydra } from './hooks/useHydra';
import { useKalshiAccount } from './hooks/useKalshiAccount';
import { usePolyAccount } from './hooks/usePolyAccount';
import { useBootSound } from './hooks/useBootSound';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { useGrantSlot } from './hooks/useGrantSlot';
import { useProfileRead } from './hooks/useProfileRead';
import { useFeeStore } from './services/hydraFees';
import { useLiveHotkeysStore } from '@/stores/liveHotkeys';
import { useTrackerPinsLifecycle } from './hooks/useTrackerPinsLifecycle';
import { useAuthStore } from './stores/authStore';
import { LoginView } from './views/Login';
import { AdminDashboard } from './views/Admin';
import { ManagerDashboard } from './views/Manager';
import { AuthShell } from './components/layout/AuthShell';
import { useBrowserDiagnostics } from './hooks/useBrowserDiagnostics';
import { ValueDashboard } from './views/ValueDashboard';
import { LiveView } from './views/Live';
import { PositionTracker } from './views/PositionTracker';
import { PositionTrackerEntry } from './views/PositionTracker/PositionTrackerEntry';
import { TraderDetail } from './views/TraderDetail';
import { OrgDetail } from './views/OrgDetail';
import { TabShell } from './components/layout/TabShell';
import { RouteErrorBoundary } from './components/layout/RouteErrorBoundary';
import { legacyRedirectRoutes } from './lib/routing/legacyRoutes';
import { useOrgScope } from './stores/orgScope';
import { Settings } from './views/Settings';
import { StreamRiftView } from './views/StreamRift';
import { MyTrades } from './views/MyTrades';
import { GameDetail } from './views/GameDetail';
import { SourcesPage } from './views/GameDetail/SourcesPage';
import { FillsPopupWindow } from './views/FillsPopup';
import G1Health from './views/G1Health';
import { CashLedgerPreview } from './views/CashLedgerPreview';
import { TraderResults } from './views/Manager/TraderResults';
import { KeyModeBanner } from './components/KeyModeBanner';
import { LimitsWidget } from './components/LimitsWidget';
import { PolyPersonalKeyClearedBanner } from './components/PolyPersonalKeyClearedBanner';
import { usePolyAccountStore } from './stores/polyAccountStore';
import { loadPolyUserConfig } from './lib/polymarket/client';
import {
  isPolyGateBannerDismissed,
  usePolyGateBannerStore,
} from './stores/polyGateBannerStore';
import { navForRole, type Role } from './types/navigation';
import { BotAccessRoute, BotOperatorRoute, BotsIndexRoute } from './components/layout/BotAccessRoute';
import { Loader2 } from 'lucide-react';
import { FeedNeedsRouteFallback } from '@/components/feed/FeedNeedsRouteFallback';
import { AccountFeedNeedsBridge } from '@/components/feed/AccountFeedNeedsBridge';

const G3View = lazy(() => import('./views/G3'));
const RecyclerView = lazy(() => import('./views/Recycler'));
const VanillaMMView = lazy(() => import('./views/VanillaMM'));
const G4View = lazy(() => import('./views/G4'));
const G9View = lazy(() => import('./views/G9'));
const HarryView = lazy(() => import('./views/Harry'));
const MartyView = lazy(() => import('./views/Marty'));
const RitaView = lazy(() => import('./views/Rita'));
const AutomationOverviewView = lazy(() => import('./views/AutomationOverview'));

/**
 * #491's fixture-only prototype, DEV BUILDS ONLY.
 *
 * `import.meta.env.DEV` is statically replaced at build time, so in a
 * production build this whole expression is `false && …`: the component is
 * never referenced, the dynamic import is never reachable, and neither the
 * view nor the synthetic slate it imports is emitted. #491 forbids
 * "production route activation", and a URL-only authenticated route is still
 * a production route (brendan, PR #525) — so production cannot reach it at
 * all rather than merely not linking to it.
 *
 * Guarded here rather than at the <Route>, so the `lazy()` call itself also
 * disappears: a module-scope `lazy(() => import(…))` const is a live
 * reference the bundler must keep, which would leave the prototype chunk in
 * the production build even with its route removed.
 */
const PropWorkbenchRoute = import.meta.env.DEV
  // PropWorkbenchLive, not PropWorkbench: the page itself is fixture-only and
  // holds no store read (#491's allowlist forbids one). The wrapper performs
  // the single read-only market-store read and passes it down (#570), so the
  // live snapshot never becomes an import inside the guarded directory.
  ? lazy(() => import('./views/PropWorkbenchLive'))
  : null;

// ── Top-level: bootstrap, then route into login / authed shell ──

function App() {
  const status = useAuthStore((s) => s.status);
  const checkBootstrap = useAuthStore((s) => s.checkBootstrap);

  useEffect(() => {
    checkBootstrap();
  }, [checkBootstrap]);

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="bg-background text-muted-foreground flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      {/* /fills lives at the top level so it skips the AuthedShell
          sidebar — the trader opens it via window.open() expecting a
          minimal popup, not a full dashboard. RequireAuthSlim still
          guards it (must be logged in) but doesn't wrap with AuthShell. */}
      <Route path="/fills" element={<FillsPopupRoute />} />
      <Route path="/*" element={<RequireAuth />} />
    </Routes>
  );
}

// Slim auth wrapper for the popup — checks user is logged in but skips
// the full AuthedShell. The popup mounts its own minimal connection
// hooks inside FillsPopupWindow.
function FillsPopupRoute() {
  const user = useAuthStore((s) => s.user);
  const status = useAuthStore((s) => s.status);
  const location = useLocation();
  if (status === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (!user) return null;
  return <FillsPopupWindow />;
}

// ── Auth gates ──────────────────────────────────────────────────

// Renders the authed shell, or kicks out to /login while remembering where
// the user was trying to go (so they land on the deep link after login).
function RequireAuth() {
  const user = useAuthStore((s) => s.user);
  const status = useAuthStore((s) => s.status);
  const location = useLocation();
  // Only a SETTLED logout kicks to /login. refresh() no longer logs out on
  // transient failures, but gate here too: a momentary null user (in-flight
  // reauth) must never unmount the desk and bounce to /login (the flicker class).
  if (status === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (!user) return null;
  return <AuthedShell />;
}

// /login is itself a route. Already-authed users get redirected to either the
// `from` location they originally tried (set by RequireAuth) or "/".
function LoginRoute() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (user) {
    // Restore search and hash too, not just the path. `RequireAuth` saves the
    // whole location, but dropping the tail silently broke any deep link that
    // survives a login — #234's `/settings#alerts` is the first one shipped, so
    // a cold load of it used to land at the top of Settings.
    const from = (location.state as { from?: Location } | null)?.from;
    const to = from ? `${from.pathname}${from.search ?? ''}${from.hash ?? ''}` : '/';
    return <Navigate to={to} replace />;
  }
  return <LoginView />;
}

// ── Authenticated app: one shell, role-gated routes ─────────────

function AuthedShell() {
  useBrowserDiagnostics();
  useHydra();
  useKalshiAccount();
  usePolyAccount();
  useBootSound();
  useFeeBootstrap();
  // Tab title follows the route (#781) — one mount for every authed page.
  useDocumentTitle();

  const role = useAuthStore((s) => s.user?.role);
  // Navigation is read-only: a restored grant keeps its nav group while the
  // session confirms it; BotAccessRoute decides whether the page mounts.
  const botNav = useGrantSlot('botAccess') !== 'hidden';

  // Every authed route needs confirmed grants (bot deep links, Settings, the
  // dashboard's one-click controls), so the profile read lives with the shell.
  useProfileRead();

  // #725 — central mount for the per-user live-trading key mapping, beside the
  // other account-scoped hooks above. It lives HERE rather than on a trading
  // page because the Settings panel, the key listener, and the teaching
  // surfaces are on three different routes; hydrating on one of them would
  // leave the others reading an empty map. Logout is handled separately by
  // authStore.purgeUserCaches, which runs on paths that unmount this shell.
  const hotkeyUserId = useAuthStore((s) => s.user?.id ?? null);
  const adminPageKey = useAuthStore((s) => s.user?.id ?? '');
  const hydrateLiveHotkeys = useLiveHotkeysStore((s) => s.hydrateFor);
  useEffect(() => {
    hydrateLiveHotkeys(hotkeyUserId);
  }, [hotkeyUserId, hydrateLiveHotkeys]);
  // #943 — per-user tracker pins: hydrated here for the same reason, since
  // Pull All on the dashboard reads the pins the tracker sets.
  useTrackerPinsLifecycle();

  // Personal-key Polymarket role gate (central mount). usePolyAccount above
  // already skips auto-connect for traders, but a user who was previously
  // an admin/manager (and saved personal Poly credentials) could still have
  // stale localStorage on a session where they re-login as a trader. This
  // effect wipes that state and surfaces a one-time banner so the user
  // understands why their saved credentials disappeared. No-op for non-
  // trader roles. Has to live AFTER usePolyAccount() so the cleanup runs
  // post-mount-effects of the upstream hooks; React fires effects in
  // declaration order.
  useEffect(() => {
    if (role !== 'trader') return;
    const stale = loadPolyUserConfig();
    if (!stale) return;
    usePolyAccountStore.getState().removeCredentials();
    if (!isPolyGateBannerDismissed()) {
      usePolyGateBannerStore.getState().setShowCleared(true);
    }
  }, [role]);
  const resolvedRole = (role ?? 'trader') as Role;
  const groups = navForRole(resolvedRole, botNav);
  const brandLetter = role === 'admin' ? 'A' : role === 'manager' ? 'M' : 'K';
  // Everyone lands on the Value Dashboard after login. Admin/manager-only
  // views are still in the sidebar — they're just not the entry point.
  const defaultPath = '/dashboard';

  return (
    <AuthShell groups={groups} brandLetter={brandLetter}>
      <RouteErrorBoundary>
      {/* Consumer feed filters: broad fallback for UNKNOWN routes; relay-held
          Poly positions/orders for the account plane on every route. */}
      <FeedNeedsRouteFallback />
      <AccountFeedNeedsBridge />
      <Routes>
        <Route index element={<Navigate to={defaultPath} replace />} />
        {/* Core trader views — share a layout that carries the KeyModeBanner. */}
        <Route element={<TraderViewLayout />}>
          <Route path="dashboard" element={<DashboardRoute />} />
          <Route path="live" element={<LiveView />} />
          <Route path="positions" element={<PositionsRoute />} />
          <Route path="positions/trader/:userId" element={<TraderDetailRoute />} />
          <Route path="positions/org/:orgId" element={<OrgDetailRoute />} />
          <Route path="trades" element={<MyTrades />} />
          {/* Legacy sport-less routes — kept so existing deep-links (MyTrades,
              Positions, Live, SpreadsTotals) keep resolving. GameDetail treats a
              missing :sport as a wildcard and a missing :gameNo as game 1. */}
          <Route path="game/:date/:teamA/:teamB" element={<GameDetail />} />
          <Route path="game/:date/:teamA/:teamB/sources" element={<SourcesPage />} />
          {/* Sport-aware routes with a doubleheader :gameNo slot (omitted for
              game 1). React Router ranks the static `sources` child above the
              dynamic `:gameNo`, so the two never collide. */}
          <Route path="game/:sport/:date/:teamA/:teamB" element={<GameDetail />} />
          <Route path="game/:sport/:date/:teamA/:teamB/sources" element={<SourcesPage />} />
          <Route path="game/:sport/:date/:teamA/:teamB/:gameNo" element={<GameDetail />} />
          <Route path="game/:sport/:date/:teamA/:teamB/:gameNo/sources" element={<SourcesPage />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        {/* Canonical route scheme: /<group>/<page>, mirroring NAV_GROUPS. Each
            multi-page group nests its pages under a shared <TabShell/>. */}
        {/* Research group (all roles). */}
        <Route path="research" element={<Navigate to="/research/g3" replace />} />
        <Route element={<TabShell />}>
          <Route path="research/streamrift" element={<StreamRiftView />} />
          <Route path="research/g3/*" element={<LazyView component={G3View} label="G3" />} />
          <Route path="research/g4/*" element={<LazyView component={G4View} label="G4" />} />
        </Route>
        {/* Bot group. Managers/Admins always enter; traders enter only when the
            existing org + user AND gate resolves botAccess for this session. */}
        <Route element={<BotAccessRoute />}>
          <Route path="bots" element={<BotsIndexRoute />} />
          <Route element={<TabShell />}>
            <Route path="bots/g9/*" element={<LazyView component={G9View} label="G9" />} />
            <Route path="bots/recycler/*" element={<LazyView component={RecyclerView} label="Recycler" />} />
            <Route element={<BotOperatorRoute />}>
              <Route path="bots/automation/*" element={<LazyView component={AutomationOverviewView} label="Automation Overview" />} />
              <Route path="bots/vanilla/*" element={<LazyView component={VanillaMMView} label="Vanilla MM" />} />
              <Route path="bots/harry/*" element={<LazyView component={HarryView} label="Harry" />} />
              <Route path="bots/marty/*" element={<LazyView component={MartyView} label="Marty" />} />
              <Route path="bots/rita/*" element={<LazyView component={RitaView} label="Rita" />} />
            </Route>
          </Route>
        </Route>
        {/* Manager-only / admin-only views (no KeyModeBanner) */}
        {(role === 'manager' || role === 'admin') && (
          <>
            {/* Organization group — TabShell owns the tab strip + the org selector.
                One Org Settings route for both roles; the page switches to admin
                mode by role. */}
            <Route path="organization" element={<Navigate to="/organization/org-settings" replace />} />
            <Route element={<TabShell />}>
              <Route
                path="organization/org-settings"
                element={<ManagerDashboard currentView="org-settings" adminMode={role === 'admin'} />}
              />
              <Route path="organization/org-account" element={<ManagerOrgAccountRoute />} />
              <Route
                path="organization/trader-results"
                element={<TraderResults adminMode={role === 'admin'} />}
              />
              <Route
                path="organization/exports"
                element={<ManagerDashboard currentView="exports" view="exports" adminMode={role === 'admin'} />}
              />
              <Route path="organization/cash-ledger-preview" element={<CashLedgerPreview />} />
            </Route>
            <Route path="organization/org-settings/trader/:userId" element={<TraderDetailRoute />} />
          </>
        )}
        {role === 'admin' && (
          <>
            {/* Administration group — Management / G1 Health share one TabShell. */}
            <Route path="administration" element={<Navigate to="/administration/management" replace />} />
            <Route element={<TabShell />}>
              {/* #1058 — keyed by admin: a sibling-tab sign-in as another admin
                  remounts the page, dropping open forms and the password banner. */}
              <Route path="administration/management" element={<AdminDashboard key={adminPageKey} />} />
              <Route path="administration/g1-health" element={<G1Health />} />
            </Route>
          </>
        )}
        {/* Prototypes — DEV BUILDS ONLY, and never in the sidebar. These are
            fixture-fed layout explorations for a product question that hasn't
            been decided yet, so production must not be able to reach one at
            all (see PropWorkbenchRoute above). Each is recorded in
            App.navigationContract.test.ts's allowance list with its reason,
            and each renders a PROTOTYPE banner. */}
        {PropWorkbenchRoute && (
          <Route
            path="prototypes/prop-workbench"
            element={<LazyView component={PropWorkbenchRoute} label="Prop Workbench" />}
          />
        )}
        {/* Legacy → canonical redirects (decoupled; see lib/routing/legacyRoutes). */}
        {legacyRedirectRoutes()}
        {/* Unknown URL → role's default landing page */}
        <Route path="*" element={<Navigate to={defaultPath} replace />} />
      </Routes>
      </RouteErrorBoundary>
    </AuthShell>
  );
}

// ── Layouts ─────────────────────────────────────────────────────

function TraderViewLayout() {
  return (
    <div className="flex h-full flex-col">
      <PolyPersonalKeyClearedBanner />
      <KeyModeBanner />
      <LimitsWidget />
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

// ── Per-route wrappers ──────────────────────────────────────────

function DashboardRoute() {
  const navigate = useNavigate();
  return <ValueDashboard onOpenPositionsPage={() => navigate('/positions')} />;
}

function PositionsRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = useAuthStore((s) => s.user?.role);
  const selectedOrgId = useOrgScope((s) => s.selectedOrgId);
  // Admins drill from the Positions page's AdminPositionsView with an org-switcher
  // selection — thread it so TraderDetail fetches THAT org's data. Managers (own
  // org) and traders leave it unset ⇒ the relay scopes to the caller.
  const scopedOrgId = role === 'admin' && selectedOrgId ? selectedOrgId : undefined;
  return (
    <PositionTrackerEntry>
      <PositionTracker
        onSelectTrader={(row) =>
          navigate(`${location.pathname}/trader/${row.userId}`, {
            state: { username: row.username, orgId: scopedOrgId },
          })
        }
        onSelectOrg={(row) =>
          navigate(`${location.pathname}/org/${row.orgId}`, {
            state: { orgName: row.name },
          })
        }
      />
    </PositionTrackerEntry>
  );
}

// Shared by all `<X>/trader/:userId` routes — works equally under
// /positions, /manager-traders, /admin-traders. Optional username/orgId
// hints come via location.state when the user clicks through; deep-link
// arrivals just see the id and the detail page fetches its own data.
function TraderDetailRoute() {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const hint = (location.state as { username?: string; orgId?: string } | null) ?? {};

  if (!userId) return <Navigate to=".." replace />;

  const back = () => {
    // /<view>/trader/<id> → /<view>
    const parent = location.pathname.replace(/\/trader\/[^/]+$/, '');
    navigate(parent || '/');
  };

  return (
    <TraderDetail
      traderId={userId}
      username={hint.username}
      orgId={hint.orgId}
      onBack={back}
    />
  );
}

function OrgDetailRoute() {
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const hint = (location.state as { orgName?: string } | null) ?? {};

  if (!orgId) return <Navigate to=".." replace />;

  const back = () => {
    const parent = location.pathname.replace(/\/org\/[^/]+$/, '');
    navigate(parent || '/');
  };

  return (
    <OrgDetail
      orgId={orgId}
      orgName={hint.orgName}
      onBack={back}
      onTraderClick={(row) => {
        // Switch from .../org/<orgId> → .../trader/<userId>; pass the orgId
        // along so the trader page knows which org context opened it.
        const traderPath = location.pathname.replace(
          /\/org\/[^/]+$/,
          `/trader/${row.userId}`,
        );
        navigate(traderPath, { state: { username: row.username, orgId } });
      }}
    />
  );
}

// Manager "Org Account" nav surface — the unfiltered master view of the
// manager's OWN org's Kalshi account (no orgId ⇒ relay scopes to caller).
// Top-level nav page, so no Back button; trader rows deep-link into the
// manager trader detail.
function ManagerOrgAccountRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = useAuthStore((s) => s.user?.role);
  const selectedOrgId = useOrgScope((s) => s.selectedOrgId);
  const scopedOrgId = role === 'admin' && selectedOrgId ? selectedOrgId : undefined;
  // When an admin opened this from the Administration org list, give them a way
  // back there — it's the surface they came from, and only admins can reach it.
  // A manager on their own Org-Account tab (or an admin browsing it via the
  // sidebar) gets no back button: it's a top-level nav tab.
  const fromAdmin =
    role === 'admin' && (location.state as { from?: string } | null)?.from === 'administration';
  // The org-selector now lives in TabShell (shared across the Organization tabs).
  return (
    <OrgDetail
      orgId={scopedOrgId}
      onBack={fromAdmin ? () => navigate('/administration/management') : undefined}
      onTraderClick={(row) => {
        // Thread the admin's switched-org scope (scopedOrgId) into the drill
        // so TraderDetail fetches the inspected org's data, not the admin's
        // own. Manager-self leaves scopedOrgId undefined ⇒ relay scopes to
        // caller (unchanged). Mirrors OrgDetailRoute + the Manager grid.
        navigate(`/organization/org-settings/trader/${row.userId}`, {
          state: { username: row.username, orgId: scopedOrgId },
        });
      }}
    />
  );
}

function LazyView({
  component: Component,
  label,
}: {
  component: React.ComponentType;
  label: string;
}) {
  return (
    <Suspense
      fallback={
        <div className="text-muted-foreground flex h-full items-center justify-center text-xs">
          Loading {label}…
        </div>
      }
    >
      <Component />
    </Suspense>
  );
}

// ── Bootstrap hooks (unchanged) ─────────────────────────────────

function useFeeBootstrap() {
  useEffect(() => {
    useFeeStore.getState().loadFees().catch((err) => {
      console.warn('[hydraFees] initial load failed:', err);
    });
  }, []);
}

export default App;
